import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import { FormattedMessage } from 'react-intl'
import ConsentManager from '../../utils/consentManager'

const BannerCard = styled.div`
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 1000;
  width: 360px;
  max-width: calc(100vw - 48px);
  font-family: inherit;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  padding: 20px;

  @media screen and (max-width: 480px) {
    bottom: 16px;
    left: 16px;
    max-width: calc(100vw - 32px);
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`

const BannerTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  padding-top: 2px;
`

const BannerDescription = styled.div`
  font-size: 13px;
  color: #555555;
  line-height: 1.5;
  margin-bottom: 16px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
`

const DeclineButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid #cccccc;
  border-radius: 4px;
  cursor: pointer;
  background-color: #ffffff;
  color: #555555;
  white-space: nowrap;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #555555;
    outline-offset: 2px;
  }
`

const AcceptButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid #cccccc;
  border-radius: 4px;
  cursor: pointer;
  background-color: #ffffff;
  color: #555555;
  white-space: nowrap;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #555555;
    outline-offset: 2px;
  }
`

const CloseButton = styled(IconButton)`
  && {
    padding: 2px;
    margin: -4px -4px 0 8px;
    color: #999999;
    flex-shrink: 0;

    &:hover {
      color: #333333;
    }
  }
`

const PolicyText = styled.p`
  margin: 16px 0 16px 0;
  font-size: 13px;
  color: #555555;
  line-height: 1.6;

  a {
    color: #1a73e8;
    text-decoration: underline;

    &:hover {
      color: #174ea6;
    }
  }
`

class CookieBanner extends Component {
  constructor(props) {
    super(props)
    this.state = {
      visible: !ConsentManager.hasConsentDecision(props.clientId),
    }
  }

  componentDidMount() {
    if (this.state.visible) {
      this.installFocusTrap()
    }
  }

  componentDidUpdate(prevProps) {
    // When the visitor moves to a different client (tenant) on the same host,
    // re-evaluate consent for that client and show the banner if no decision
    // has been made for it yet.
    if (prevProps.clientId !== this.props.clientId) {
      const visible = !ConsentManager.hasConsentDecision(this.props.clientId)
      if (visible !== this.state.visible) {
        this.setState({ visible })
        if (visible) {
          this.installFocusTrap()
        } else {
          this.removeFocusTrap()
        }
      }
    }
  }

  componentWillUnmount() {
    this.removeFocusTrap()
  }

  installFocusTrap() {
    this.handleFocusTrap = (e) => {
      const banner = document.getElementById('cookie-banner')
      if (!banner) return
      const focusable = banner.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', this.handleFocusTrap)
  }

  removeFocusTrap() {
    if (this.handleFocusTrap) {
      document.removeEventListener('keydown', this.handleFocusTrap)
      this.handleFocusTrap = null
    }
  }

  getPolicies() {
    const { privacyPolicyUrl, cookiesPolicyUrl } = this.props
    return { privacyPolicyUrl, cookiesPolicyUrl }
  }

  handleAcceptAll = () => {
    this.removeFocusTrap()
    ConsentManager.acceptAll(this.props.clientId, this.getPolicies())
    if (this.props.onAcceptAll) {
      this.props.onAcceptAll()
    }
    this.setState({ visible: false })
  }

  handleDecline = () => {
    this.removeFocusTrap()
    ConsentManager.rejectAll(this.props.clientId, this.getPolicies())
    if (this.props.onDecline) {
      this.props.onDecline()
    }
    this.setState({ visible: false })
  }

  handleClose = () => {
    this.removeFocusTrap()
    if (this.props.onClose) {
      this.props.onClose()
    }
    this.setState({ visible: false })
  }

  renderPolicyLinks() {
    const { privacyPolicyUrl, cookiesPolicyUrl } = this.props

    if (!privacyPolicyUrl && !cookiesPolicyUrl) {
      return null
    }

    const cookieLinkElement = cookiesPolicyUrl ? (
      <a
        href={cookiesPolicyUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Cookies Policy"
      >
        <FormattedMessage id="cookieBanner.cookiesPolicyLinkText" defaultMessage="Cookie Policy" />
      </a>
    ) : null

    const privacyLinkElement = privacyPolicyUrl ? (
      <a
        href={privacyPolicyUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Privacy Policy"
      >
        <FormattedMessage id="cookieBanner.privacyPolicyLinkText" defaultMessage="Privacy Policy" />
      </a>
    ) : null

    if (privacyPolicyUrl && cookiesPolicyUrl) {
      return (
        <PolicyText>
          <FormattedMessage
            id="cookieBanner.readMoreBoth"
            defaultMessage="Read more about our {cookieLink} and {privacyLink}."
            values={{
              cookieLink: cookieLinkElement,
              privacyLink: privacyLinkElement,
            }}
          />
        </PolicyText>
      )
    }

    if (privacyPolicyUrl) {
      return (
        <PolicyText>
          <FormattedMessage
            id="cookieBanner.readMorePrivacy"
            defaultMessage="Read more about our {privacyLink}."
            values={{
              privacyLink: privacyLinkElement,
            }}
          />
        </PolicyText>
      )
    }

    if (cookiesPolicyUrl) {
      return (
        <PolicyText>
          <FormattedMessage
            id="cookieBanner.readMoreCookies"
            defaultMessage="Read more about our {cookieLink}."
            values={{
              cookieLink: cookieLinkElement,
            }}
          />
        </PolicyText>
      )
    }

    return null
  }

  render() {
    const { visible } = this.state

    if (!visible) {
      return null
    }

    return (
      <BannerCard
        id="cookie-banner"
        role="dialog"
        aria-modal="true"
        aria-label="Cookie Policy"
        aria-describedby="cookie-banner-desc"
      >
        <CardHeader>
          <BannerTitle>
            <FormattedMessage id="cookieBanner.title" defaultMessage="Cookie Consent" />
          </BannerTitle>
          <CloseButton
            id="cookie-banner-close"
            onClick={this.handleClose}
            aria-label="Close cookie banner"
          >
            <CloseIcon fontSize="small" />
          </CloseButton>
        </CardHeader>
        <BannerDescription id="cookie-banner-desc">
          <FormattedMessage
            id="cookieBanner.description"
            defaultMessage='By clicking "Accept All", you agree to the storing of cookies on your device to enhance site navigation, analyze site usage, and show you relevant job opportunities on other sites.'
          />
        </BannerDescription>
        {this.renderPolicyLinks()}
        <ButtonGroup>
          <DeclineButton
            id="cookie-banner-decline"
            onClick={this.handleDecline}
            aria-label="Decline cookies"
          >
            <FormattedMessage id="cookieBanner.decline" defaultMessage="Decline" />
          </DeclineButton>
          <AcceptButton
            id="cookie-banner-accept"
            onClick={this.handleAcceptAll}
            aria-label="Accept all cookies"
          >
            <FormattedMessage id="cookieBanner.acceptAll" defaultMessage="Accept All" />
          </AcceptButton>
        </ButtonGroup>
      </BannerCard>
    )
  }
}

CookieBanner.propTypes = {
  onAcceptAll: PropTypes.func,
  onDecline: PropTypes.func,
  onClose: PropTypes.func,
  privacyPolicyUrl: PropTypes.string,
  cookiesPolicyUrl: PropTypes.string,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

CookieBanner.defaultProps = {
  onAcceptAll: undefined,
  onDecline: undefined,
  onClose: undefined,
}

export default CookieBanner
